import { useMemo } from 'react'
import { buildForm, type PeekRecipe } from '@solum/experience'
import { formatDate, formatMoney } from '@solum/format'
import { PeekPanel } from '@solum/ui'
import {
  displayLabel, useCanonicalForm, useEntityIdentities, useEntityRow, useSchema, useSemantics,
} from '../data/queries'
import { formLayout } from './formLayout'
import s from './PeekRow.module.css'

/**
 * The HOST half of Peek (#2281): it resolves, authorizes and formats; the
 * renderer draws.
 *
 * ── it composes the record's own form, it does not restate one ─────────────
 * The sections come from `buildForm(schema, semantics, entity, canonicalForm)`
 * — the exact call `RecordBody` makes for the full record — and their identities
 * from `formLayout`, which is the identity core 2026091520 canonicalized and
 * stored. So the preview's fields, labels, order and classification are the
 * record's, because they are literally the same resolution.
 *
 * A field the caller may not read never arrives: `useEntityRow` is the governed
 * read and RLS decides its columns. Nothing here widens a door, adds a filter or
 * invents a field — there is no query in this file.
 *
 * ── which sections, and the fallback ───────────────────────────────────────
 * The recipe names section IDS. With no recipe, #2281's rule is "the first
 * visible, non-empty section in the resolved form order" — decided HERE because
 * only the host holds the resolved form, and never by inferring from a name.
 *
 * A recipe naming a section the form no longer resolves is REPORTED, not
 * quietly dropped: #2281 asks for repair with an explicit warning rather than
 * silent substitution, and a preview that showed fewer sections than it was
 * asked for would be a governed document disagreeing with itself in silence.
 */
export function PeekRow({
  entity, id, recipe, onOpenRecord, testId = 'peek-panel',
}: {
  entity: string
  id: string
  /** The target's declared recipe, as the slot resolver handed it over. */
  recipe: PeekRecipe | null
  onOpenRecord?: () => void
  testId?: string
}) {
  const schema = useSchema()
  const sem = useSemantics(entity)
  const canonical = useCanonicalForm(entity)
  const row = useEntityRow(entity, id)
  const identities = useEntityIdentities()

  const resolved = useMemo(() => {
    if (!schema.data || !sem.data) return null
    const document = canonical.data && typeof canonical.data === 'object'
      && Array.isArray((canonical.data as { sections?: unknown }).sections)
      ? canonical.data as Parameters<typeof buildForm>[3]
      : undefined
    const view = buildForm(schema.data, sem.data, entity, document)
    // `formLayout` drops sections with no resolvable fields before assigning
    // ids, which is the same rule the stored identity was canonicalized under.
    return formLayout(view)
  }, [schema.data, sem.data, canonical.data, entity])

  const { sections, missing } = useMemo(() => {
    if (!resolved) return { sections: [], missing: [] as string[] }
    const byId = new Map(resolved.document.sections.map((s) => [s.id, s]))
    const wanted = recipe?.sections ?? []
    if (wanted.length === 0) {
      // No recipe: the FIRST visible non-empty section, in the resolved order.
      const first = resolved.document.sections[0]
      return { sections: first ? [first] : [], missing: [] }
    }
    return {
      sections: wanted.map((sid) => byId.get(sid)).filter((s): s is NonNullable<typeof s> => !!s),
      missing: wanted.filter((sid) => !byId.has(sid)),
    }
  }, [resolved, recipe])

  const values = (row.data ?? {}) as Record<string, unknown>

  // The `{}` fallback lives INSIDE the memo: as a `?? {}` above it, it was a new
  // object on every render where nothing resolved, so the memo it fed never held.
  const shaped = useMemo(() => {
    const labels = resolved?.labels ?? {}
    return sections.map((section) => ({
      title: section.label,
      layoutId: section.id,
      layoutColumns: undefined,
      fields: section.items.map((item) => ({
        field: item.key,
        label: labels[item.key] ?? item.key,
        layoutSpan: item.span,
      })),
    }))
  }, [sections, resolved])

  /* EVERY read that decides this render is classified, failure included.

     A schema or semantics read that FAILED leaves `resolved` null and the
     panel would render with no sections — which is the empty state, and says
     "this record has nothing to show" about a form nobody managed to read.
     Four reads, one refusal sentence, and none of them able to fall through
     to a reassuring blank. */
  const refused = row.isError || canonical.isError || schema.isError || sem.isError
  const busy = row.isLoading || canonical.isLoading || schema.isLoading || sem.isLoading

  /* THE TITLE IS NEVER THE UUID (#1705, fixed here #2351).
     This read `values.name ?? values.display_name ?? id`, so every entity whose
     name column is neither of those — an address, an engagement, a tender —
     titled its own preview with the record's uuid. `displayLabel` is the one
     definition of a row's name, and the entity's governed SINGULAR label turns
     the unresolved case into the ruled `Unnamed address` rather than either a
     uuid or a claim that a record we just read is unavailable. */
  const typeLabel = identities.data?.find((e) => e.entity_type === entity)?.singular_label ?? null

  return (
    <PeekPanel
      title={refused ? 'Record unavailable' : displayLabel(values, typeLabel)}
      sections={shaped}
      busy={busy}
      problem={refused
        ? 'This record could not be read here. It may be refused, or the form may not resolve.'
        : missing.length > 0
          ? `This preview names ${missing.length === 1 ? 'a section' : 'sections'} the form no longer has (${missing.join(', ')}). Repair the preview in Entity Settings.`
          : undefined}
      /* A LABEL/VALUE ROW. These two spans were unstyled, so every field read
         as one word — `First NameChris` — in this lens and in the relationship
         workspace's preview alike, which is where the governed raster caught
         it. `PeekPanel` styles the panel and leaves the row to its host; this
         is the host drawing one. */
      renderField={(field) => {
        const value = values[field.field]
        const kind = sem.data?.fields?.find?.((f: { column_name?: string }) => f.column_name === field.field)
        return (
          <div className={s.field} data-testid={`${testId}-field-${field.field}`}>
            <span className={s.label}>{field.label}</span>
            <span className={s.value} data-testid={`${testId}-value-${field.field}`}>
              {present(value, kind)}
            </span>
          </div>
        )
      }}
      onOpenRecord={onOpenRecord}
      testId={testId}
    />
  )
}

/**
 * One value, read-only, through the SAME formatters the rest of the console
 * uses. A blank is an em dash rather than an empty cell, so "no value" and "the
 * column is missing" do not look identical.
 */
function present(value: unknown, field?: { kind?: string }): string {
  if (value == null || value === '') return '—'
  switch (field?.kind) {
    case 'money': return formatMoney(value)
    case 'date': case 'datetime': return formatDate(value)
    case 'boolean': return value ? 'Yes' : 'No'
    default: return String(value)
  }
}
